import React, { useState } from "react";

/**
 * Friendly inline editor for seller intelligence — replaces the raw JSON
 * textarea as the primary editing surface. Every section is editable with
 * placeholder-guided inputs:
 *   - chips with × for string lists (competitors, disqualifiers, keywords,
 *     industries) plus an inline "type + Enter" add-input
 *   - preset toggle-chips for ICP employee/revenue ranges (fixed vocabulary —
 *     these must match the List Builder's option ids exactly, so free text
 *     would be a footgun)
 *   - cards/rows with labelled inputs for personas, pains, signals, proof points
 *
 * Raw JSON stays available behind the small "Advanced: edit raw JSON" link for
 * power users (onAdvanced) — it's no longer the front door.
 *
 * Used by SellerIntelligencePanel and SellerIntelligenceCard.
 */

const EMPLOYEE_RANGE_OPTIONS = [
  "1 - 10", "11 - 20", "21 - 50", "51 - 100", "101 - 200", "201 - 500",
  "501 - 1000", "1001 - 2000", "2001 - 5000", "5001 - 10000", "10000+"
];
const REVENUE_RANGE_OPTIONS = [
  { id: "0-99999", label: "$0 - $0.1M" },
  { id: "100000-499999", label: "$0.1M - $0.5M" },
  { id: "500000-999999", label: "$0.5M - $1M" },
  { id: "1000000-4999999", label: "$1M - $5M" },
  { id: "5000000-9999999", label: "$5M - $10M" },
  { id: "10000000-24999999", label: "$10M - $25M" },
  { id: "25000000-49999999", label: "$25M - $50M" },
  { id: "50000000-99999999", label: "$50M - $100M" },
  { id: "100000000-499999999", label: "$100M - $500M" },
  { id: "500000000-999999999", label: "$500M - $1B" },
  { id: "1000000000-4999999999", label: "$1B - $5B" },
  { id: "5000000000-9999999999", label: "$5B - $10B" },
  { id: "10000000000+", label: "$10B+" }
];

const deepCopy = (v) => JSON.parse(JSON.stringify(v || {}));

// ── Small building blocks ────────────────────────────────────────────

// String-list editor: removable chips + "type and press Enter" input.
const ChipListEditor = ({ items, onChange, placeholder }) => {
  const [text, setText] = useState("");
  const list = Array.isArray(items) ? items : [];

  const commit = () => {
    const v = text.trim();
    if (!v) return;
    if (!list.includes(v)) onChange([...list, v]);
    setText("");
  };

  return (
    <div className="siEdit__chipList">
      {list.map((s, i) => (
        <span key={i} className="siEdit__chip">
          {s}
          <button
            type="button"
            className="siEdit__chipX"
            onClick={() => onChange(list.filter((_, j) => j !== i))}
            aria-label={`Remove ${s}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="siEdit__chipInput"
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          }
          if (e.key === "Backspace" && !text && list.length) {
            onChange(list.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
};

// Fixed-vocabulary toggle chips (ICP employee/revenue ranges).
const PresetChipToggle = ({ options, selected, onChange }) => {
  const sel = Array.isArray(selected) ? selected : [];
  const toggle = (id) =>
    onChange(sel.includes(id) ? sel.filter((v) => v !== id) : [...sel, id]);
  return (
    <div className="siEdit__chipList">
      {options.map((opt) => {
        const id = typeof opt === "string" ? opt : opt.id;
        const label = typeof opt === "string" ? opt : opt.label;
        const on = sel.includes(id);
        return (
          <button
            key={id}
            type="button"
            className={`siEdit__presetChip ${on ? "siEdit__presetChip--on" : ""}`}
            onClick={() => toggle(id)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="siEdit__field">
    {label && <label className="siEdit__label">{label}</label>}
    {children}
  </div>
);

const SectionBox = ({ title, hint, onAdd, addLabel, children }) => (
  <div className="siEdit__section">
    <div className="siEdit__sectionHead">
      <h4>{title}</h4>
      {onAdd && (
        <button type="button" className="siEdit__addBtn" onClick={onAdd}>
          + {addLabel || "Add"}
        </button>
      )}
    </div>
    {hint && <p className="siEdit__hint">{hint}</p>}
    {children}
  </div>
);

// ── Editor ───────────────────────────────────────────────────────────

const SellerIntelligenceEditor = ({ intel, saving, onSave, onCancel, onAdvanced }) => {
  const [draft, setDraft] = useState(() => deepCopy(intel));

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setRow = (key, idx, field, value) =>
    setDraft((d) => {
      const arr = [...(d[key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...d, [key]: arr };
    });
  const removeRow = (key, idx) =>
    setDraft((d) => ({ ...d, [key]: (d[key] || []).filter((_, i) => i !== idx) }));
  const addRow = (key, empty) =>
    setDraft((d) => ({ ...d, [key]: [...(d[key] || []), empty] }));

  const cmap = draft.competitor_map || {};
  const setCmap = (bucket, value) =>
    set("competitor_map", { ...cmap, [bucket]: value });
  const icp = draft.ideal_customer_profile || {};
  const setIcp = (field, value) =>
    set("ideal_customer_profile", { ...icp, [field]: value });

  // Drop rows whose key field was left empty so a stray "+ Add" never saves junk.
  const handleSave = () => {
    const cleaned = {
      ...draft,
      canonical_buyer_personas: (draft.canonical_buyer_personas || []).filter((p) => (p.title || "").trim()),
      anti_personas: (draft.anti_personas || []).filter((a) => (a.title_pattern || "").trim()),
      pain_taxonomy: (draft.pain_taxonomy || []).filter((p) => (p.pain || "").trim()),
      buying_signals_taxonomy: (draft.buying_signals_taxonomy || []).filter((s) => (s.signal_kind || "").trim()),
      proof_points: (draft.proof_points || []).filter((p) => (p.customer_segment || "").trim())
    };
    onSave(cleaned);
  };

  return (
    <div className="siEdit">
      <SectionBox title="Category pitch">
        <textarea
          className="siEdit__textarea"
          rows={2}
          value={draft.category_pitch_one_liner || ""}
          placeholder="Your positioning in one sharp sentence…"
          onChange={(e) => set("category_pitch_one_liner", e.target.value)}
        />
      </SectionBox>

      <SectionBox
        title="✅ Buyer personas"
        hint="The roles that buy, use or champion your product."
        onAdd={() => addRow("canonical_buyer_personas", { title: "", seniority: "", department: "", why_this_persona: "", title_keywords: [] })}
        addLabel="Add persona"
      >
        {(draft.canonical_buyer_personas || []).map((p, i) => (
          <div key={i} className="siEdit__card">
            <button type="button" className="siEdit__removeBtn" onClick={() => removeRow("canonical_buyer_personas", i)} aria-label="Remove persona">×</button>
            <div className="siEdit__grid3">
              <Field label="Title">
                <input type="text" value={p.title || ""} placeholder="e.g. VP of Sales" onChange={(e) => setRow("canonical_buyer_personas", i, "title", e.target.value)} />
              </Field>
              <Field label="Seniority">
                <input type="text" value={p.seniority || ""} placeholder="e.g. VP" onChange={(e) => setRow("canonical_buyer_personas", i, "seniority", e.target.value)} />
              </Field>
              <Field label="Department">
                <input type="text" value={p.department || ""} placeholder="e.g. Sales" onChange={(e) => setRow("canonical_buyer_personas", i, "department", e.target.value)} />
              </Field>
            </div>
            <Field label="Why this persona">
              <textarea className="siEdit__textarea" rows={2} value={p.why_this_persona || ""} placeholder="Why does this role buy or champion your product?" onChange={(e) => setRow("canonical_buyer_personas", i, "why_this_persona", e.target.value)} />
            </Field>
            <Field label="Title keywords">
              <ChipListEditor items={p.title_keywords} placeholder="Add keyword… e.g. revenue" onChange={(v) => setRow("canonical_buyer_personas", i, "title_keywords", v)} />
            </Field>
          </div>
        ))}
      </SectionBox>

      <SectionBox
        title="🚫 Anti-personas"
        hint="Roles never worth reaching out to."
        onAdd={() => addRow("anti_personas", { title_pattern: "", why_not: "" })}
        addLabel="Add anti-persona"
      >
        {(draft.anti_personas || []).map((a, i) => (
          <div key={i} className="siEdit__row">
            <input type="text" value={a.title_pattern || ""} placeholder="e.g. HR titles" onChange={(e) => setRow("anti_personas", i, "title_pattern", e.target.value)} />
            <input type="text" value={a.why_not || ""} placeholder="Why not…" onChange={(e) => setRow("anti_personas", i, "why_not", e.target.value)} />
            <button type="button" className="siEdit__removeBtn siEdit__removeBtn--inline" onClick={() => removeRow("anti_personas", i)} aria-label="Remove">×</button>
          </div>
        ))}
      </SectionBox>

      <SectionBox title="⚔️ Competitors">
        <Field label="Direct (we replace)">
          <ChipListEditor items={cmap.direct} placeholder="Add direct competitor…" onChange={(v) => setCmap("direct", v)} />
        </Field>
        <Field label="Adjacent (overlap)">
          <ChipListEditor items={cmap.adjacent} placeholder="Add adjacent competitor…" onChange={(v) => setCmap("adjacent", v)} />
        </Field>
        <Field label="Complementary (we integrate)">
          <ChipListEditor items={cmap.complementary} placeholder="Add complementary tool…" onChange={(v) => setCmap("complementary", v)} />
        </Field>
      </SectionBox>

      <SectionBox
        title="📉 Pains we solve"
        onAdd={() => addRow("pain_taxonomy", { pain: "", evidence_we_solve_it: "", language_to_use: "" })}
        addLabel="Add pain"
      >
        {(draft.pain_taxonomy || []).map((p, i) => (
          <div key={i} className="siEdit__card">
            <button type="button" className="siEdit__removeBtn" onClick={() => removeRow("pain_taxonomy", i)} aria-label="Remove pain">×</button>
            <Field label="Pain">
              <input type="text" value={p.pain || ""} placeholder="e.g. Reps waste hours switching tools" onChange={(e) => setRow("pain_taxonomy", i, "pain", e.target.value)} />
            </Field>
            <div className="siEdit__grid2">
              <Field label="How we solve it">
                <input type="text" value={p.evidence_we_solve_it || ""} placeholder="Evidence we solve it…" onChange={(e) => setRow("pain_taxonomy", i, "evidence_we_solve_it", e.target.value)} />
              </Field>
              <Field label="Buyer's words">
                <input type="text" value={p.language_to_use || ""} placeholder='e.g. "tool fatigue"' onChange={(e) => setRow("pain_taxonomy", i, "language_to_use", e.target.value)} />
              </Field>
            </div>
          </div>
        ))}
      </SectionBox>

      <SectionBox
        title="📡 Buying signals"
        onAdd={() => addRow("buying_signals_taxonomy", { signal_kind: "", urgency_default: "medium", why_it_triggers_us: "" })}
        addLabel="Add signal"
      >
        {(draft.buying_signals_taxonomy || []).map((s, i) => (
          <div key={i} className="siEdit__row siEdit__row--signal">
            <input type="text" value={s.signal_kind || ""} placeholder="e.g. cro_hire" onChange={(e) => setRow("buying_signals_taxonomy", i, "signal_kind", e.target.value)} />
            <select value={s.urgency_default || "medium"} onChange={(e) => setRow("buying_signals_taxonomy", i, "urgency_default", e.target.value)}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <input type="text" value={s.why_it_triggers_us || ""} placeholder="Why it triggers us…" onChange={(e) => setRow("buying_signals_taxonomy", i, "why_it_triggers_us", e.target.value)} />
            <button type="button" className="siEdit__removeBtn siEdit__removeBtn--inline" onClick={() => removeRow("buying_signals_taxonomy", i)} aria-label="Remove">×</button>
          </div>
        ))}
      </SectionBox>

      <SectionBox title="🛑 Account disqualifiers">
        <ChipListEditor
          items={draft.account_disqualifiers}
          placeholder="Add disqualifier… e.g. <$5M ARR"
          onChange={(v) => set("account_disqualifiers", v)}
        />
      </SectionBox>

      <SectionBox
        title="🏆 Proof points"
        onAdd={() => addRow("proof_points", { customer_segment: "", outcome_metric: "", source_hint: "" })}
        addLabel="Add proof point"
      >
        {(draft.proof_points || []).map((p, i) => (
          <div key={i} className="siEdit__row siEdit__row--signal">
            <input type="text" value={p.customer_segment || ""} placeholder="Customer / segment…" onChange={(e) => setRow("proof_points", i, "customer_segment", e.target.value)} />
            <input type="text" value={p.outcome_metric || ""} placeholder="Outcome… e.g. +32% replies" onChange={(e) => setRow("proof_points", i, "outcome_metric", e.target.value)} />
            <input type="text" value={p.source_hint || ""} placeholder="Source (optional)" onChange={(e) => setRow("proof_points", i, "source_hint", e.target.value)} />
            <button type="button" className="siEdit__removeBtn siEdit__removeBtn--inline" onClick={() => removeRow("proof_points", i)} aria-label="Remove">×</button>
          </div>
        ))}
      </SectionBox>

      <SectionBox
        title="🎯 Ideal customer profile"
        hint="Powers the Find prospects search — industries, company size and revenue."
      >
        <Field label="Industries">
          <ChipListEditor items={icp.industries} placeholder="Add industry… e.g. Computer Software" onChange={(v) => setIcp("industries", v)} />
        </Field>
        <Field label="Company size (employees)">
          <PresetChipToggle options={EMPLOYEE_RANGE_OPTIONS} selected={icp.employee_ranges} onChange={(v) => setIcp("employee_ranges", v)} />
        </Field>
        <Field label="Annual revenue">
          <PresetChipToggle options={REVENUE_RANGE_OPTIONS} selected={icp.revenue_ranges} onChange={(v) => setIcp("revenue_ranges", v)} />
        </Field>
      </SectionBox>

      <div className="siEdit__footer">
        {onAdvanced && (
          <button type="button" className="siEdit__advancedLink" onClick={onAdvanced}>
            Advanced: edit raw JSON
          </button>
        )}
        <div className="siEdit__footerRight">
          <button type="button" className="siPanel__btn ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="siPanel__btn primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerIntelligenceEditor;
